import api from './api';
import type { ChatMessage } from '../types';

export interface ChatMessagesResponse {
    application_id: number;
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    messages: ChatMessage[];
}

class ChatService {
    /**
     * Get chat messages for an application with pagination
     */
    async getMessages(
        applicationId: number,
        page: number = 1,
        pageSize: number = 50
    ): Promise<ChatMessagesResponse> {
        const response = await api.get<ChatMessagesResponse>(
            `/chat/${applicationId}/messages`,
            { params: { page, page_size: pageSize } }
        );
        return response.data;
    }

    /**
     * Send a text message
     */
    async sendTextMessage(applicationId: number, content: string): Promise<ChatMessage> {
        const response = await api.post<ChatMessage>(
            `/chat/${applicationId}/messages`,
            { content }
        );
        return response.data;
    }

    /**
     * Send a file message
     */
    async sendFileMessage(applicationId: number, file: File): Promise<ChatMessage> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post<ChatMessage>(
            `/chat/${applicationId}/messages/file`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    }

    /**
     * Load all messages (handles pagination internally)
     */
    async loadAllMessages(applicationId: number): Promise<ChatMessage[]> {
        const allMessages: ChatMessage[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
            const response = await this.getMessages(applicationId, page, 50);
            allMessages.push(...response.messages);
            hasMore = page < response.total_pages;
            page++;
        }

        // Sort messages by created_at (oldest first)
        return allMessages.sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
    }
}

export default new ChatService();
